import { CoreInterfaces, DTOs } from "./../core/Models";
import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import { saveAs } from "file-saver";
import * as Constants from "./../core/Constants";
import expressions from "angular-expressions";
import {
  contactPersonMapper,
  contactPersonWithEmailMapper,
  findQuestionInService,
  mapUserResponseToCustomerContactPersons,
  formatOrganisationNo,
  mapUserResponseToGTContactPersons,
  formatSwedishPostalCode,
  formatSwedishPostalCodeInAddressLine,
  getCompanyAddressCountry,
} from "./shared";
import {
  getFrequencyMultiplicator,
  getPayrollSystem,
  mapServiceCodesToServicesDTOs,
} from "./calculation";
import { createEngagementTableRows } from "./engagement-utils";
import i18n from "src/i18n";
import { fetchPdfFile } from "./api";
import { formatDate } from "./utils";
import { mapQuestionCodesToQuestionDTOs } from "./question-utils";
import { t } from "i18next";
import { isServiceTaskGroupSelected } from "./task-utils";
import { resolveDigitalSignerNameForEngagementLetter } from "./generate-documents";
import { v4 as uuid } from "uuid";
import { ClientDetails } from "src/core/Models/Core.interface";
export declare const DocxtemplaterHtmlModule: any;

function getFormattedPostalNumber(globalState: CoreInterfaces.AppState) {
  return formatSwedishPostalCode(
    globalState.generalVariables.postalNumber,
    getCompanyAddressCountry(globalState.remoteData?.companyDetails)
  );
}

function angularParser(tag: any) {
  if (tag === ".") {
    return {
      get: (s: any) => s,
    };
  }
  const expr = expressions.compile(tag.replace(/(’|“|”)/g, "'"));
  return {
    get: (s: any) => expr(s),
  };
}

export function loadFile(
  url: string,
  callback: (err: Error, data: string) => void
) {
  PizZipUtils.getBinaryContent(url, callback);
}

function replaceErrors(key: string, value: any) {
  if (value instanceof Error) {
    return Object.getOwnPropertyNames(value).reduce(function (
      error: any,
      key: string
    ) {
      error[key] = (value as any)[key];
      return error;
    },
    {});
  }
  return value;
}

export async function generateEngagementDescriptionDocx(
  globalState: CoreInterfaces.AppState,
  t: Function,
  language: string
) {
  return generateEngagementDescriptionWordDocument(globalState, t, language);
}

export async function generateEngagementDescriptionWordDocument(
  globalState: CoreInterfaces.AppState,
  t: Function,
  language: string
): Promise<any> {
  const initialLanguage = i18n.language;
  await i18n.changeLanguage(language.toLowerCase());
  return new Promise((resolve, reject) => {
    loadFile(
      `/resources/template_${language}.docx`,
      (error: Error, content: string) => {
        if (error) {
          i18n.changeLanguage(initialLanguage);
          return reject(error);
        } else {
          const zip = new PizZip(content);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            parser: angularParser,
            modules: [new DocxtemplaterHtmlModule({})],
          });
          const templateData = formDocxTemplaterPack(globalState, t);
          doc.setData(templateData);
          try {
            doc.render();
            i18n.changeLanguage(initialLanguage);
            return resolve(doc);
          } catch (error: any) {
            i18n.changeLanguage(initialLanguage);
            return reject(error);
          }
        }
      }
    );
  });
}

export async function generatePdfDocument(
  globalState: CoreInterfaces.AppState,
  dispatchFn: Function,
  t: Function,
  language: string,
  updateApplicationLoadingStateFn: (
    loadingState: boolean,
    loadingMessage: string
  ) => void
): Promise<void> {
  return generateWordDocument(
    globalState,
    dispatchFn,
    t,
    language,
    t("General.GeneratingDocument"),
    (doc, filename) => {
      const formdata = new FormData();
      const out = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      formdata.append("file", out);
      return fetchPdfFile(
        Constants.APIPath.ConvertToPdf,
        Constants.APIMethod.POST,
        formdata
      ).then((response) => {
        saveAs(response, filename);
      });
    },
    updateApplicationLoadingStateFn
  );
}

export async function generateWordDocument(
  globalState: CoreInterfaces.AppState,
  dispatchFn: Function,
  t: Function,
  language: string,
  loadingMessage: string,
  callbackFn: (doc: any, filename: string) => Promise<any>,
  updateApplicationLoadingStateFn: (
    loadingState: boolean,
    loadingMessage: string
  ) => void
): Promise<void> {
  updateApplicationLoadingStateFn(true, loadingMessage);
  dispatchFn({
    type: Constants.AppStateActions.LoadingBlurredStateUpdate,
    payload: {
      isPageBlurred: true,
    },
  });
  const initialLanguage = i18n.language;
  await i18n.changeLanguage(language.toLowerCase());
  loadFile(
    `/resources/template_${language}.docx`,
    (error: Error, content: string) => {
      if (error) {
        i18n.changeLanguage(initialLanguage);
        updateApplicationLoadingStateFn(false, null);
        throw error;
      }
      const zip = new PizZip(content);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        parser: angularParser,
        modules: [new DocxtemplaterHtmlModule({})],
      });
      const templateData = formDocxTemplaterPack(globalState, t);
      doc.setData(templateData);
      try {
        doc.render();
      } catch (error: any) {
        i18n.changeLanguage(initialLanguage);
        console.log(JSON.stringify({ error: error }, replaceErrors));

        if (error.properties && error.properties.errors instanceof Array) {
          const errorMessages = error.properties.errors
            .map(function (error: any) {
              return error.properties.explanation;
            })
            .join("\n");
          console.log("errorMessages", errorMessages);
        }
        throw error;
      }
      return callbackFn(
        doc,
        t("General.Filename", {
          companyName: globalState.generalVariables.companyName,
          orgNr: globalState.generalVariables.organisationNo,
        })
      )
        .catch((err) => {
          throw err;
        })
        .finally(() => {
          i18n.changeLanguage(initialLanguage);
          updateApplicationLoadingStateFn(false, null);
          dispatchFn({
            type: Constants.AppStateActions.LoadingBlurredStateUpdate,
            payload: {
              isPageBlurred: false,
            },
          });
        });
    }
  );
}

export function generateEngagementDocument(
  documentType: Constants.EngagementDocumentType,
  templateData: object,
  language: string
): Promise<any> {
  const fileName = `template_${documentType}_${language}`;
  const generateFilePromise = new Promise((resolve, reject) => {
    loadFile(`/resources/${fileName}.docx`, (error: Error, content: string) => {
      if (error) {
        reject("Cannot read template file");
      } else {
        try {
          const zip = new PizZip(content);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            parser: angularParser,
            modules: [new DocxtemplaterHtmlModule({})],
          });
          doc.setData(templateData);
          doc.render();
          resolve(doc);
        } catch (error: any) {
          if (error.properties && error.properties.errors instanceof Array) {
            const errorMessages = error.properties.errors
              .map(function (error: any) {
                return error.properties.explanation;
              })
              .join("\n");
            reject(errorMessages);
          } else {
            reject(error);
          }
        }
      }
    });
  });
  return generateFilePromise;
}

function getContactPerson(
  contactPersonList: Array<CoreInterfaces.ContactPersonItem>,
  service: DTOs.ServiceDTO,
  questionCode: Constants.QuestionCode
): string {
  const contactPersonQuestion = findQuestionInService(service, questionCode);
  const contactPerson = contactPersonList.find(
    (item) => item.key === contactPersonQuestion.data.userValue
  );
  if (!!contactPerson) {
    return `${contactPerson.firstName} ${contactPerson.lastName}`;
  }
  return "";
}

function getQuestionValueAsNumber(
  service: DTOs.ServiceDTO,
  questionCode: Constants.QuestionCode
): number {
  const question = findQuestionInService(service, questionCode);
  const value = question?.state?.isShown ? +question.data.userValue : 0;

  return value;
}

function formDocxTemplaterPack(
  globalState: CoreInterfaces.AppState,
  t: Function
): CoreInterfaces.DocxTemplaterPack {
  const servicesMap = mapServiceCodesToServicesDTOs(globalState, [
    Constants.ServiceCode.AccountsReceivable,
    Constants.ServiceCode.AccountsPayable,
    Constants.ServiceCode.CorporateCardManagement,
    Constants.ServiceCode.OtherAccountAndReconciliation,
    Constants.ServiceCode.PeriodReporting,
    Constants.ServiceCode.InterimListedCompanies,
    Constants.ServiceCode.AnnualReporting,
    Constants.ServiceCode.AnnualReportingListedCompanies,
    Constants.ServiceCode.PayrollAndExpenseAndTravelInvoiceManagement,
    Constants.ServiceCode.AnnualPayrollRoutines,
    Constants.ServiceCode.StartUpAccounting,
    Constants.ServiceCode.StartUpPayroll,
    Constants.ServiceCode.ExtraServices,
  ]);
  const accountReceivableService = servicesMap.AccountsReceivable;
  const accountPayableService = servicesMap.AccountsPayable;
  const corporateCardManagementService = servicesMap.CorporateCardManagement;
  const otherAccountingAndReconciliationService =
    servicesMap.OtherAccountAndReconciliation;
  const periodReportingService = servicesMap.PeriodReporting;
  const interimReportingListedCompaniesService =
    servicesMap.InterimListedCompanies;
  const annualAccoutingService = servicesMap.AnnualReporting;
  const annualReportingListedCompaniesService =
    servicesMap.AnnualReportingListedCompanies;
  const payrollAndExpenseAndTravelInvoiceManagementService =
    servicesMap.PayrollAndExpenseAndTravelInvoiceManagement;
  const annualPayrollRoutines = servicesMap.AnnualPayrollRoutines;

  const result: CoreInterfaces.DocxTemplaterPack = {
    BusinessArea: t(
      `Options.BusinessArea.EngagementDescriptionDocument.${globalState.currentConfiguration.businessUnit}`
    ),
    CompanyName: globalState.generalVariables.companyName,
    OrganisationNumber: formatOrganisationNo(
      globalState.generalVariables.organisationNo
    ),
    AccountingSystem: globalState.generalVariables.accountingSystem,
    PayrollSystem: getPayrollSystem(globalState) ?? "",
    ValidFromDate: formatDate(
      new Date(globalState.currentConfiguration.validFromDate),
      Constants.HelpfulConstants.DateFormatYearMonthAndDay
    ),
    ValidUntilDate: globalState.currentConfiguration.validUntilDate
      ? formatDate(
          new Date(globalState.currentConfiguration.validUntilDate),
          Constants.HelpfulConstants.DateFormatYearMonthAndDay
        )
      : null,
    CurrentYear: new Date().getFullYear(),
    AccountReceivable: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        accountReceivableService,
        Constants.AccountReceivableQuestion.Q0120
      ),
      NumberOfUnits: getQuestionValueAsNumber(
        accountReceivableService,
        Constants.AccountReceivableQuestion.Q0101
      ),
      IsVisible: accountReceivableService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        accountReceivableService,
        t
      ),
    },
    AccountPayable: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        accountPayableService,
        Constants.AccountPayableQuestion.Q0221
      ),
      NumberOfUnits: getQuestionValueAsNumber(
        accountPayableService,
        Constants.AccountPayableQuestion.Q0201
      ),
      IsVisible: accountPayableService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(globalState, accountPayableService, t),
    },
    CorporateCardManagement: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        corporateCardManagementService,
        Constants.CorporateCardManagementQuestion.Q0311
      ),
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        corporateCardManagementService,
        t
      ),
      NumberOfCorporateCards: getQuestionValueAsNumber(
        corporateCardManagementService,
        Constants.CorporateCardManagementQuestion.Q0301
      ),
      NumberOfReceipts: getQuestionValueAsNumber(
        corporateCardManagementService,
        Constants.CorporateCardManagementQuestion.Q0302
      ),
      IsVisible: corporateCardManagementService.state.isSelected,
    },
    OtherAccountingAndReconciliation: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        otherAccountingAndReconciliationService,
        Constants.OtherAccountAndReconciliationQuestions.Q0407
      ),
      NumberOfVouchers: getQuestionValueAsNumber(
        otherAccountingAndReconciliationService,
        Constants.OtherAccountAndReconciliationQuestions.Q0404
      ),
      NumberOfBalanceAccounts: getQuestionValueAsNumber(
        otherAccountingAndReconciliationService,
        Constants.OtherAccountAndReconciliationQuestions.Q0405
      ),
      IsVisible: otherAccountingAndReconciliationService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        otherAccountingAndReconciliationService,
        t
      ),
    },
    MonthlyQuartelyReporting: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        periodReportingService,
        Constants.PeriodReportingQuestion.Q0513
      ),
      IsVisible: periodReportingService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(globalState, periodReportingService, t),
    },
    InterimReportingListedCompanies: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        periodReportingService,
        Constants.PeriodReportingQuestion.Q0513
      ),
      IsVisible: periodReportingService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        interimReportingListedCompaniesService,
        t
      ),
    },
    AnnualAccouting: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        annualAccoutingService,
        Constants.AnnualReportingQuestions.Q0608
      ),
      NumberOfTransactions: getQuestionValueAsNumber(
        annualAccoutingService,
        Constants.AnnualReportingQuestions.Q0602
      ),
      NumberOfBalanceAccounts: getFrequencyMultiplicator(
        annualAccoutingService.data.tasks.find(
          (taskDTO) =>
            taskDTO.data.code === Constants.AnnualReportingTasks.T0612
        ),
        annualAccoutingService
      ),
      IsVisible: annualAccoutingService.state.isSelected,
      AnnualReportTasks: formDocxTemplaterTasksPack(
        globalState,
        annualAccoutingService,
        t,
        Constants.TaskGroup.AnnualReport
      ),
      AnnualReportV2Tasks: formDocxTemplaterTasksPack(
        globalState,
        annualAccoutingService,
        t,
        Constants.TaskGroup.AnnualReportV2
      ),
      AnnualReportV3Tasks: formDocxTemplaterTasksPack(
        globalState,
        annualAccoutingService,
        t,
        Constants.TaskGroup.AnnualReportV3
      ),
      CorporateIncomeTaxTasks: formDocxTemplaterTasksPack(
        globalState,
        annualAccoutingService,
        t,
        Constants.TaskGroup.CorporateIncomeTax
      ),
      StatutoryAnnualReportTasks: formDocxTemplaterTasksPack(
        globalState,
        annualAccoutingService,
        t,
        Constants.TaskGroup.StatutoryAnnualReport
      ),
    },
    AnnualAccoutsFiscalYearClosingListedCompanies: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        annualAccoutingService,
        Constants.AnnualReportingQuestions.Q0608
      ),
      IsVisible: annualAccoutingService.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        annualReportingListedCompaniesService,
        t
      ),
    },
    Payroll: {
      ContactPerson: getContactPerson(
        globalState.remoteData.customerContactPersons,
        payrollAndExpenseAndTravelInvoiceManagementService,
        Constants.PayrollAndExpenseAndTravelInvoiceManagementQuestions.Q0763
      ),
      IsVisible:
        payrollAndExpenseAndTravelInvoiceManagementService.state.isSelected,
      NumberOfPayslips: getQuestionValueAsNumber(
        payrollAndExpenseAndTravelInvoiceManagementService,
        Constants.PayrollAndExpenseAndTravelInvoiceManagementQuestions.Q0701
      ),
      AdditionalPayrollServicesTasks: formDocxTemplaterTasksPack(
        globalState,
        payrollAndExpenseAndTravelInvoiceManagementService,
        t,
        Constants.TaskGroup.AdditionalPayrollServices
      ),
      MonthlyPayrollRunTasks: formDocxTemplaterTasksPack(
        globalState,
        payrollAndExpenseAndTravelInvoiceManagementService,
        t,
        Constants.TaskGroup.MonthlyPayrollRun
      ),
      MonthlyPayrollReconciliationTasks: formDocxTemplaterTasksPack(
        globalState,
        payrollAndExpenseAndTravelInvoiceManagementService,
        t,
        Constants.TaskGroup.MonthlyPayrollReconciliation
      ),
      PayrollServicesInAdditionTasks: formDocxTemplaterTasksPack(
        globalState,
        payrollAndExpenseAndTravelInvoiceManagementService,
        t,
        Constants.TaskGroup.PayrollServicesInAddition
      ),
      MonthlyReportingPayrollTasks: formDocxTemplaterTasksPack(
        globalState,
        payrollAndExpenseAndTravelInvoiceManagementService,
        t,
        Constants.TaskGroup.MonthlyReportingPayroll
      ),
    },
    AnnualPayrollRoutines: {
      ContactPerson: null,
      IsVisible: annualPayrollRoutines.state.isSelected,
      Tasks: formDocxTemplaterTasksPack(globalState, annualPayrollRoutines, t),
    },
    ExtraServices: {
      IsVisible: servicesMap[
        Constants.ServiceCode.ExtraServices
      ].data.tasks.some((task) => task.state.isVisible === true),
      Tasks: formDocxTemplaterTasksPack(
        globalState,
        servicesMap[Constants.ServiceCode.ExtraServices],
        t
      ),
    },
  };
  return result;
}

function formDocxTemplaterTasksPack(
  globalState: CoreInterfaces.AppState,
  service: DTOs.ServiceDTO,
  t: Function,
  taskGroup: Constants.TaskGroup = Constants.TaskGroup.DefaultGroup
): Array<CoreInterfaces.DocxTemplaterTaskPack> {
  const extraData: CoreInterfaces.ServiceTaskProcessorExtraData = {
    generalVariables: globalState.generalVariables,
    extraServices: globalState.services,
  };
  const rows = createEngagementTableRows(service, extraData);
  return rows
    .filter(
      (row) =>
        row.taskDTO.data.serviceTaskGroup === taskGroup &&
        row.taskDTO.state.isActive
    )
    .map((row) => {
      return <CoreInterfaces.DocxTemplaterTaskPack>{
        Description: row.title,
        Frequency: t(`Options.${row.frequency}`),
        AllocationOfResponsability: t(
          `Options.${row.allocationOfResponsibility}`
        ),
        TimeDue: row.taskDTO.data.timeDue,
        Comment: row.comment,
      };
    });
}

function extractContactPersonUserDetails(
  globalState: CoreInterfaces.AppState,
  question: DTOs.QuestionDTO
): Array<CoreInterfaces.SigneeDetailsDocxPack> {
  if (question.state.isShown) {
    if (
      Array.isArray(question.data.userValue) &&
      question.data.userValue.length > 0
    ) {
      return (
        question.data.userValue as CoreInterfaces.ContactPersonUserDetails[]
      ).map((userDetails: CoreInterfaces.ContactPersonUserDetails) => {
        const contactPerson =
          globalState.remoteData.customerContactPersons.find(
            (contactPerson) => contactPerson.key === userDetails.ContactPersonId
          );
        return {
          Name: `${contactPerson.firstName} ${contactPerson.lastName}`,
          Email: contactPerson.email,
          PersonalNumber: userDetails.PersonalNumber,
          IsSigned: userDetails.IsSigned,
          Uuid: userDetails.Uuid,
        };
      });
    }
  }
  return [
    {
      Name: "",
      Email: "",
      PersonalNumber: "",
      IsSigned: false,
      Uuid: uuid(),
    },
  ];
}

function extractGTContactPersonUserDetails(
  globalState: CoreInterfaces.AppState,
  question: DTOs.QuestionDTO
): Array<string> {
  if (question.state.isShown) {
    if (
      Array.isArray(question.data.userValue) &&
      question.data.userValue.length > 0
    ) {
      return (question.data.userValue as string[]).map(
        (userDetails: string) => {
          const contactPerson = globalState.remoteData.gtContactPersons.find(
            (contactPerson) => contactPerson.key === userDetails.toString()
          );
          return `${contactPerson.firstName} ${contactPerson.lastName}`;
        }
      );
    }
  }
  return [""];
}

export function distributeSigneesAcrossTwoArrays(
  contactPersonSignees: Array<string | any>
) {
  const array1: Array<string> = [];
  const array2: Array<string> = [];
  contactPersonSignees.map((value, index) => {
    if (index % 2 === 0) {
      array1.push(value);
    } else {
      array2.push(value);
    }
  });
  return [array1, array2];
}

export function handleTemplateEngagementLetter(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO
) {
  const {
    Q0003,
    Q0014,
    Q0017,
    Q0018,
    Q0023,
    Q0024,
    Q0025,
    Q0029,
    Q0030,
    Q0031,
    Q0032,
    Q0033,
    Q0034,
    Q0035,
    Q0036,
    Q0037,
  } = Constants.GeneralInformationQuestion;
  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    Q0003,
    Q0014,
    Q0017,
    Q0018,
    Q0023,
    Q0024,
    Q0025,
    Q0029,
    Q0030,
    Q0031,
    Q0032,
    Q0033,
    Q0034,
    Q0035,
    Q0036,
    Q0037,
  ]);
  const servicesMap = mapServiceCodesToServicesDTOs(globalState, [
    Constants.ServiceCode.GeneralInformation,
    Constants.ServiceCode.AccountsReceivable,
    Constants.ServiceCode.AccountsPayable,
    Constants.ServiceCode.CorporateCardManagement,
    Constants.ServiceCode.OtherAccountAndReconciliation,
    Constants.ServiceCode.PeriodReporting,
    Constants.ServiceCode.InterimListedCompanies,
    Constants.ServiceCode.AnnualReporting,
    Constants.ServiceCode.AnnualReportingListedCompanies,
    Constants.ServiceCode.PayrollAndExpenseAndTravelInvoiceManagement,
    Constants.ServiceCode.AnnualPayrollRoutines,
    Constants.ServiceCode.StartUpAccounting,
    Constants.ServiceCode.StartUpPayroll,
  ]);
  const periodReportingService = servicesMap.PeriodReporting;
  const annualReportingService = servicesMap.AnnualReporting;
  const payrollAndExpenseAndTravelInvoiceManagementService =
    servicesMap.PayrollAndExpenseAndTravelInvoiceManagement;

  const gTPreparesServices =
    periodReportingService.state.isSelected &&
    periodReportingService.data.questions.some(
      (question) =>
        [
          Constants.PeriodReportingQuestion.Q0504,
          Constants.PeriodReportingQuestion.Q0506,
          Constants.PeriodReportingQuestion.Q0507,
          Constants.PeriodReportingQuestion.Q0508,
        ].includes(question.data.code as Constants.PeriodReportingQuestion) &&
        question.state.isShown &&
        question.data.userValue === Constants.YesNo.Yes
    );

  const separator = t("General.DocumentsSeparator");

  const contactPersonUserDetails = extractContactPersonUserDetails(
    globalState,
    questionDTOMap.Q0034
  );

  const allContactPersonsSigned = contactPersonUserDetails.every(
    (contactPerson) => contactPerson.IsSigned === true
  );
  const contactPersonUserDetailsSigned = contactPersonUserDetails.filter(
    (cp) => cp.IsSigned === true
  );
  const contactPersonUserDetailsUnsigned = contactPersonUserDetails.filter(
    (cp) => cp.IsSigned === false
  );

  const contactPersonUserDetailsUnsignedParsed = parseContactPersonList(
    contactPersonUserDetailsUnsigned,
    separator
  );
  const contactPersonUserDetailsSignedParsed = parseContactPersonList(
    contactPersonUserDetailsSigned,
    separator
  );

  const q0504 = findQuestionInService(
    periodReportingService,
    Constants.PeriodReportingQuestion.Q0504
  );
  const q0514 = findQuestionInService(
    periodReportingService,
    Constants.PeriodReportingQuestion.Q0514
  );
  const q0601 = findQuestionInService(
    annualReportingService,
    Constants.AnnualReportingQuestions.Q0601
  );
  const q0610 = findQuestionInService(
    annualReportingService,
    Constants.AnnualReportingQuestions.Q0610
  );
  const isCheckboxSelectedAnnualAccounts = isServiceTaskGroupSelected(
    q0610,
    Constants.PartOfAnnualAccounts.AnnualAccounts
  );
  const isCheckboxSelectedAnnualReport = isServiceTaskGroupSelected(
    q0610,
    Constants.PartOfAnnualAccounts.AnnualReport
  );
  const isCheckboxSelectedCompanyDeclaration = isServiceTaskGroupSelected(
    q0610,
    Constants.PartOfAnnualAccounts.CompanyDeclaration
  );
  const q0733 = findQuestionInService(
    payrollAndExpenseAndTravelInvoiceManagementService,
    Constants.PayrollAndExpenseAndTravelInvoiceManagementQuestions.Q0733
  );
  const q0765 = findQuestionInService(
    payrollAndExpenseAndTravelInvoiceManagementService,
    Constants.PayrollAndExpenseAndTravelInvoiceManagementQuestions.Q0765
  );

  const isOneOfTheFirstServicesSelected =
    servicesMap.AccountsPayable.state.isSelected ||
    servicesMap.AccountsReceivable.state.isSelected ||
    servicesMap.CorporateCardManagement.state.isSelected ||
    servicesMap.OtherAccountAndReconciliation.state.isSelected;
  const isService4Or5Selected =
    servicesMap.OtherAccountAndReconciliation.state.isSelected ||
    servicesMap.PeriodReporting.state.isSelected;
  const isService5bSelected =
    servicesMap.InterimListedCompanies.state.isSelected;
  const isService6Or6BSelected =
    servicesMap.AnnualReporting.state.isSelected ||
    servicesMap.AnnualReportingListedCompanies.state.isSelected;
  const isService7Selected =
    payrollAndExpenseAndTravelInvoiceManagementService.state.isSelected;
  const isService8Selected = servicesMap.StartUpAccounting.state.isSelected;
  const isService9Selected = servicesMap.StartUpPayroll.state.isSelected;
  const isOneOfTheFirstFiveServicesSelected =
    isOneOfTheFirstServicesSelected ||
    isService4Or5Selected ||
    isService5bSelected;
  const isAnyServiceSelected =
    isOneOfTheFirstFiveServicesSelected ||
    isService6Or6BSelected ||
    isService8Selected ||
    isService7Selected ||
    isService9Selected;

  const isAccountingServiceSelected =
    isOneOfTheFirstFiveServicesSelected ||
    isService6Or6BSelected ||
    isService8Selected;
  let amountInEngagementLetter = "";
  if (questionDTOMap.Q0024.state.isShown) {
    amountInEngagementLetter = questionDTOMap.Q0024.data.userValue as string;
  } else {
    if (questionDTOMap.Q0025.state.isShown) {
      const [firstInput, secondInput] = questionDTOMap.Q0025.data
        .userValue as unknown as string[];
      amountInEngagementLetter = `${firstInput}-${secondInput}`;
    }
  }
  const isFeeTable =
    questionDTOMap.Q0023.state.isShown &&
    questionDTOMap.Q0023.data.userValue === Constants.FeeExpression.Table;
  const isActiveOptionForDigitalSigning =
    questionDTOMap.Q0017.state.isShown &&
    questionDTOMap.Q0017.data.userValue === Constants.YesNo.Yes;
  const isInactiveOptionForDigitalSigning =
    questionDTOMap.Q0017.state.isShown &&
    questionDTOMap.Q0017.data.userValue === Constants.YesNo.No;
  const validUntilDate = globalState.currentConfiguration.validUntilDate;
  const validUntil = validUntilDate
    ? formatDate(
        validUntilDate,
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      )
    : "";
  let contactPersonInEngagementLetter: Array<string> = null;
  if (questionDTOMap.Q0036.state.isShown) {
    const q0036ContactPersons = mapUserResponseToCustomerContactPersons(
      globalState,
      questionDTOMap[Q0036]
    );
    contactPersonInEngagementLetter = q0036ContactPersons.map(
      (contactPersonItem: CoreInterfaces.ContactPersonItem) =>
        `${contactPersonItem.firstName} ${contactPersonItem.lastName}, ${contactPersonItem.email}`
    );
  }
  const contactPersonGT = globalState.remoteData.gtContactPersons.find(
    (eachCustomerContactPerson) => {
      return (
        eachCustomerContactPerson.key ==
        questionDTOMap[Q0003].data.userValue.toString()
      );
    }
  );
  let servicesPerformedForEmployees = null;
  if (questionDTOMap.Q0035.state.isShown) {
    servicesPerformedForEmployees = t(
      `Options.${questionDTOMap.Q0035.data.userValue}`
    );
  }
  let contactPersonGtName = "";
  let contactPersonGtNameWithEmail = "";
  if (!!contactPersonGT) {
    contactPersonGtName = `${contactPersonGT.firstName} ${contactPersonGT.lastName}`;
    contactPersonGtNameWithEmail = `${contactPersonGT.firstName} ${contactPersonGT.lastName}, ${contactPersonGT.email}`;
  }

  let q0018ContactPersonNames: Array<string> = [];

  let engagementClientSigneesLeftColumn: Array<string> = [];
  let engagementClientSigneesRightColumn: Array<string> = [];

  if (questionDTOMap.Q0018.state.isShown) {
    const q0018ContactPersons = mapUserResponseToCustomerContactPersons(
      globalState,
      questionDTOMap[Q0018]
    );
    q0018ContactPersonNames = q0018ContactPersons.map(
      (contactPersonItem: CoreInterfaces.ContactPersonItem) =>
        `${contactPersonItem.firstName} ${contactPersonItem.lastName}`
    );
    [engagementClientSigneesLeftColumn, engagementClientSigneesRightColumn] =
      distributeSigneesAcrossTwoArrays(q0018ContactPersonNames);
  }
  const digitallySigningUserName =
    resolveDigitalSignerNameForEngagementLetter(globalState);

  const templateDataEngagementLetter: CoreInterfaces.TemplateDataEngagementLetter =
    {
      accountancyTable: isFeeTable && isAccountingServiceSelected,
      address: globalState.generalVariables.address,
      allContactPersonsSigned: allContactPersonsSigned,
      amountInEngagementLetter: amountInEngagementLetter,
      area: globalState.generalVariables.area,
      isOneOfTheFirstFiveServicesSelected: isOneOfTheFirstFiveServicesSelected,
      assistanceFromGT:
        gTPreparesServices ||
        (isService7Selected &&
          q0733.state.isShown &&
          q0733.data.userValue === Constants.YesNo.Yes),
      companyName: globalState.currentConfiguration.companyName,
      contactPersonGTForContractor: contactPersonGtNameWithEmail,
      contactPersonGT: contactPersonGtName,
      contactPersonInEngagementLetter: parseContactPersonList(
        contactPersonInEngagementLetter,
        separator
      ),
      contactPersonUserDetails: contactPersonUserDetails,
      contactPersonUserDetailsSigned: contactPersonUserDetailsSigned,
      contactPersonUserDetailsUnsigned: contactPersonUserDetailsUnsigned,
      contactPersonUserDetailsSignedParsed:
        contactPersonUserDetailsSignedParsed,
      contactPersonUserDetailsUnsignedParsed:
        contactPersonUserDetailsUnsignedParsed,
      currentYear: new Date().getFullYear(),
      digitallySignedBy: digitallySigningUserName,
      feePeriodBeloppOrInterval:
        (questionDTOMap.Q0023.state.isShown &&
          questionDTOMap.Q0023.data.userValue ===
            Constants.FeeExpression.Interval) ||
        questionDTOMap.Q0023.data.userValue === Constants.FeeExpression.Amount,
      gTPreparesEcList:
        q0504.state.isShown && q0504.data.userValue === Constants.YesNo.Yes,
      gTPreparesTaxes:
        (isService7Selected &&
          q0733.state.isShown &&
          q0733.data.userValue === Constants.YesNo.Yes) ||
        (periodReportingService.state.isSelected &&
          q0514.state.isShown &&
          q0514.data.userValue === Constants.YesNo.Yes),
      isActiveOptionForDigitalSigning: isActiveOptionForDigitalSigning,
      isAnnualReportsSelected: isCheckboxSelectedAnnualReport,
      isAnnualAccountsSelected: isCheckboxSelectedAnnualAccounts,
      isAnyServiceSelected: isAnyServiceSelected,
      isCompanyDeclarationSelected: isCheckboxSelectedCompanyDeclaration,
      isCompanyDeclarationSelectedOrShouldGTDoIncomeTax:
        isCheckboxSelectedCompanyDeclaration ||
        (questionDTOMap.Q0033.state.isShown &&
          questionDTOMap.Q0033.data.userValue === Constants.YesNo.Yes),
      isFeeOnCurrentAccount:
        questionDTOMap.Q0023.state.isShown &&
        questionDTOMap.Q0023.data.userValue ===
          Constants.FeeExpression.OnCurrentAccount,
      isFeeTable: isFeeTable,
      isInactiveOptionForDigitalSigning: isInactiveOptionForDigitalSigning,
      isSelectedService4Or5: isService4Or5Selected,
      isService6Or6BSelected: isService6Or6BSelected,
      isService7Selected: isService7Selected,
      isService8Selected: isService8Selected,
      isService8Or9Selected: isService8Selected || isService9Selected,
      isService9Selected: isService9Selected,
      isOneOfFirst4ServicesSelectedOrService6:
        isOneOfTheFirstServicesSelected ||
        (q0601.state.isShown && q0601.data.userValue === Constants.YesNo.Yes),
      isUsedAnotherAccountingSystem:
        (!isService7Selected ||
          !q0765.state.isShown ||
          (q0765.state.isShown &&
            q0765.data.userValue !== Constants.YesNo.No)) &&
        questionDTOMap.Q0014.state.isShown &&
        questionDTOMap.Q0014.data.userValue === Constants.YesNo.No,
      isUsedAnotherPayrollSystem:
        isService7Selected &&
        q0765.state.isShown &&
        q0765.data.userValue === Constants.YesNo.No,
      numberOfMonths:
        questionDTOMap.Q0032.state.isShown &&
        t(
          `Options.${questionDTOMap.Q0032.data.userValue as unknown as string}`
        ),
      notAllContactPersonsSigned: !allContactPersonsSigned,
      notOnlyService7Selected:
        isOneOfTheFirstFiveServicesSelected ||
        isService6Or6BSelected ||
        isService8Selected ||
        isService9Selected,
      onlyService7Selected:
        !(
          isOneOfTheFirstFiveServicesSelected ||
          isService6Or6BSelected ||
          isService8Selected ||
          isService9Selected
        ) && isService7Selected,
      organisationNo: formatOrganisationNo(
        globalState.currentConfiguration.organisationNumber
      ),
      postalNumber: getFormattedPostalNumber(globalState),
      payrollTable: isFeeTable && (isService7Selected || isService9Selected),
      servicesPerformedForEmployees: servicesPerformedForEmployees,
      shouldGTDoIncomeTax:
        questionDTOMap.Q0033.state.isShown &&
        questionDTOMap.Q0033.data.userValue === Constants.YesNo.Yes,
      shouldNotGTDoIncomeTax:
        questionDTOMap.Q0033.state.isShown &&
        questionDTOMap.Q0033.data.userValue === Constants.YesNo.No,
      shouldOwnerAnalysisBeUsed:
        questionDTOMap.Q0037.state.isShown &&
        questionDTOMap.Q0037.data.userValue === Constants.YesNo.Yes,
      validityCurrent:
        questionDTOMap.Q0029.state.isShown &&
        questionDTOMap.Q0029.data.userValue === Constants.FeeExpression.Current,
      validityPeriod:
        questionDTOMap.Q0029.state.isShown &&
        questionDTOMap.Q0029.data.userValue === Constants.FeeExpression.Period,
      validFrom: formatDate(
        globalState.currentConfiguration.validFromDate,
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
      validUntil: validUntil,
      engagementClientSigneesLeftColumn: engagementClientSigneesLeftColumn,
      engagementClientSigneesRightColumn: engagementClientSigneesRightColumn,
      isOneOfFirstSixServicesSelected:
        isOneOfTheFirstFiveServicesSelected ||
        isService6Or6BSelected ||
        isService5bSelected,
    };

  return templateDataEngagementLetter;
}

export function handleTemplateEngagementCounselingLetter(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO
) {
  const {
    Q0003,
    Q0014,
    Q0017,
    Q0018,
    Q0031,
    Q0034,
    Q0038,
    Q0039,
    Q0040,
    Q0041,
    Q0042,
  } = Constants.GeneralInformationQuestion;
  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    Q0003,
    Q0014,
    Q0017,
    Q0018,
    Q0031,
    Q0034,
    Q0038,
    Q0039,
    Q0040,
    Q0041,
    Q0042,
  ]);

  let contactPersonInEngagementLetter: Array<string> = [];
  let contactPersonInEngagementLetterSigneesOnly: Array<string> = [];
  let q0018ContactPersonNames: Array<string> = [];
  let q0034ContactPersonUserDetails: Array<CoreInterfaces.SigneeDetailsDocxPack> =
    [];
  let contactPersonGTForContractor = "";
  let contactPersonGT = "";

  const contactPersonGTForContractorValue =
    globalState.remoteData.gtContactPersons.find(
      (eachCustomerContactPerson) => {
        return (
          eachCustomerContactPerson.key ==
          questionDTOMap[Q0003].data.userValue.toString()
        );
      }
    );
  if (contactPersonGTForContractorValue) {
    contactPersonGTForContractor = [contactPersonGTForContractorValue].map(
      contactPersonWithEmailMapper
    )[0];
    contactPersonGT = [contactPersonGTForContractorValue].map(
      contactPersonMapper
    )[0];
  }

  if (questionDTOMap.Q0018.state.isShown) {
    const q0018ContactPersons = mapUserResponseToCustomerContactPersons(
      globalState,
      questionDTOMap[Q0018]
    );
    q0018ContactPersonNames = q0018ContactPersons.map(contactPersonMapper);
    if (q0018ContactPersons.length > 0) {
      contactPersonInEngagementLetter = q0018ContactPersons.map(
        contactPersonWithEmailMapper
      );
    }
  }

  if (questionDTOMap.Q0034.state.isShown) {
    q0034ContactPersonUserDetails = extractContactPersonUserDetails(
      globalState,
      questionDTOMap[Q0034]
    );
    if (q0034ContactPersonUserDetails.length > 0) {
      contactPersonInEngagementLetter = q0034ContactPersonUserDetails.map(
        (contactPerson) => `${contactPerson.Name}, ${contactPerson.Email}`
      );
      contactPersonInEngagementLetterSigneesOnly = q0034ContactPersonUserDetails
        .filter((contactPerson) => contactPerson.IsSigned)
        .map((contactPerson) => `${contactPerson.Name}`);
    }
  }

  const digitallySigningUserName =
    resolveDigitalSignerNameForEngagementLetter(globalState);

  const separator = t("General.DocumentsSeparator");

  const templateDataEngagementLetter: CoreInterfaces.TemplateDataEngagementCounselingLetter =
    {
      address: globalState.generalVariables.address,
      area: globalState.generalVariables.area,
      companyName: globalState.generalVariables.companyName,
      contactPersonGT: contactPersonGT,
      contactPersonInEngagementLetter: parseContactPersonList(
        contactPersonInEngagementLetter,
        separator
      ),
      contactPersonInEngagementLetterSigneesOnly: parseContactPersonList(
        contactPersonInEngagementLetterSigneesOnly,
        separator
      ),
      contactPersonGTForContractor: contactPersonGTForContractor,
      currentYear: new Date().getFullYear(),
      engagementClientSignees: questionDTOMap.Q0034.state.isShown
        ? q0034ContactPersonUserDetails
            .filter((contactPerson) => contactPerson.IsSigned)
            .map((contactPerson) => contactPerson.Name)
        : questionDTOMap.Q0018.state.isShown
        ? q0018ContactPersonNames
        : [],
      contactPersonUserDetails: questionDTOMap.Q0034.state.isShown
        ? q0034ContactPersonUserDetails
        : [],
      isActiveOptionForDigitalSigning:
        questionDTOMap.Q0017.state.isShown &&
        questionDTOMap.Q0017.data.userValue === Constants.YesNo.Yes,
      digitallySignedBy: digitallySigningUserName,
      isAnalysisOfAdministrativeProcessesEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.Analysis,
      isEconomicsHandbookEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.Economics,
      isActiveBoardWorkEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.ActiveBoardWork,
      isCfoCoachEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.CFO_Coaching,
      isBudgetProcessEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.BudgetingProcess,
      isOwnerDirectiveEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.OwnerDirectives,
      isStartingSustainabilityWorkInSMEsEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.StartingSustainabilityWorkInSme,
      isOtherEngagementCounseling:
        questionDTOMap.Q0038.state.isShown &&
        questionDTOMap.Q0038.data.userValue ===
          Constants.CounselingAssignment.Other,
      isGTActingAsDataProcessor:
        questionDTOMap.Q0040.state.isShown &&
        questionDTOMap.Q0040.data.userValue === Constants.YesNo.Yes,
      isGTWorkingInClientSystem:
        questionDTOMap.Q0039.state.isShown &&
        questionDTOMap.Q0039.data.userValue === Constants.YesNo.Yes,
      isInactiveOptionForDigitalSigning:
        questionDTOMap.Q0017.state.isShown &&
        questionDTOMap.Q0017.data.userValue === Constants.YesNo.No,
      isIntervalFormatOfTheFee:
        questionDTOMap.Q0042.state.isShown &&
        questionDTOMap.Q0042.data.userValue ===
          Constants.FeeExpression.IntervalFee,
      isPeriodFormatOfTheFee:
        questionDTOMap.Q0042.state.isShown &&
        questionDTOMap.Q0042.data.userValue ===
          Constants.FeeExpression.PeriodFee,
      isProjectDescriptionAttached:
        questionDTOMap.Q0041.state.isShown &&
        questionDTOMap.Q0041.data.userValue === Constants.YesNo.Yes,
      organisationNo: formatOrganisationNo(
        globalState.currentConfiguration.organisationNumber
      ),
      postalNumber: getFormattedPostalNumber(globalState),
      validityPeriod: questionDTOMap.Q0031.state.isShown,
      validFrom: formatDate(
        globalState.currentConfiguration.validFromDate,
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
      validUntil: formatDate(
        globalState.currentConfiguration.validUntilDate,
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
    };

  return templateDataEngagementLetter;
}

export function handleTemplateAuditLetter(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO,
  language: string
) {
  const {
    QA0006,
    Q0017,
    Q0018,
    QA0009,
    QA0010,
    QA0011,
    QA0012,
    QA0013,
    QA0014,
    QA0015,
    QA0016,
    QA0017,
    QA0018,
    QA0019,
    QA0021,
    QA0022,
    QA0023,
    QA0024,
    QA0021_1,
    QA0022_1,
    QA0023_1,
    QA0024_1,
    QA0025,
    QA0026,
  } = Constants.GeneralInformationQuestion;
  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    QA0006,
    Q0017,
    Q0018,
    QA0009,
    QA0010,
    QA0011,
    QA0012,
    QA0013,
    QA0014,
    QA0015,
    QA0016,
    QA0017,
    QA0018,
    QA0019,
    QA0021,
    QA0022,
    QA0023,
    QA0024,
    QA0021_1,
    QA0022_1,
    QA0023_1,
    QA0024_1,
    QA0025,
    QA0026,
  ]);

  let q0018ContactPersonNames: Array<string> = [];
  let engagementClientSigneesLeftColumn: Array<string> = [];
  let engagementClientSigneesRightColumn: Array<string> = [];
  if (questionDTOMap.Q0018.state.isShown) {
    const q0018ContactPersons = mapUserResponseToCustomerContactPersons(
      globalState,
      questionDTOMap[Q0018]
    );
    q0018ContactPersonNames = q0018ContactPersons.map(
      (contactPersonItem: CoreInterfaces.ContactPersonItem) =>
        `${contactPersonItem.firstName} ${contactPersonItem.lastName}`
    );
    [engagementClientSigneesLeftColumn, engagementClientSigneesRightColumn] =
      distributeSigneesAcrossTwoArrays(q0018ContactPersonNames);
  }

  const feeFormat = questionDTOMap.QA0013.data.userValue;
  const feeFormatValue = (() => {
    switch (feeFormat) {
      case Constants.FormatFeeAuditLetter.Alternative1:
        return 1;
      case Constants.FormatFeeAuditLetter.Alternative2:
        return 2;
      case Constants.FormatFeeAuditLetter.Alternative3:
        return 3;
      case Constants.FormatFeeAuditLetter.Alternative4:
        return 4;
    }
  })();

  let rule = "";
  let rule2 = "";
  let priceText = "";
  let duration = "";
  if (questionDTOMap.QA0015.state.isShown) {
    priceText = questionDTOMap.QA0015.data.userValue as string;
  }

  if (questionDTOMap.QA0021.state.isShown) {
    rule = questionDTOMap.QA0021.data.userValue as string;
  } else {
    if (questionDTOMap.QA0021_1.state.isShown) {
      rule = questionDTOMap.QA0021_1.data.userValue as string;
    }
  }

  if (questionDTOMap.QA0024.state.isShown) {
    rule2 = questionDTOMap.QA0024.data.userValue as string;
  } else {
    if (questionDTOMap.QA0024_1.state.isShown) {
      rule2 = questionDTOMap.QA0024_1.data.userValue as string;
    }
  }

  if (questionDTOMap.QA0016.state.isShown) {
    duration = questionDTOMap.QA0016.data.userValue as string;
  } else {
    duration = questionDTOMap.QA0025.data.userValue as string;
  }

  let assigner = "";
  let assigneeDecisionMakingBody = "";
  let receiver = "";
  let administrator = "";

  if (questionDTOMap.QA0019.state.isShown) {
    assigner = questionDTOMap.QA0019.data.userValue as string;
  }
  if (questionDTOMap.QA0026.state.isShown) {
    assigneeDecisionMakingBody = questionDTOMap.QA0026.data.userValue as string;
  }

  if (questionDTOMap.QA0023.state.isShown) {
    receiver = questionDTOMap.QA0023.data.userValue as string;
  } else {
    if (questionDTOMap.QA0023_1.state.isShown) {
      receiver = questionDTOMap.QA0023_1.data.userValue as string;
    }
  }

  if (questionDTOMap.QA0022.state.isShown) {
    administrator = questionDTOMap.QA0022.data.userValue as string;
  } else {
    if (questionDTOMap.QA0022_1.state.isShown) {
      administrator = questionDTOMap.QA0022_1.data.userValue as string;
    }
  }

  const selectedService = globalState.services.filter(
    (service: DTOs.ServiceDTO) =>
      service.state.isSelected &&
      service.data.code !== Constants.ServiceCode.GeneralInformation
  )[0];

  const gtSigneeName = resolveDigitalSignerNameForEngagementLetter(globalState);

  const templateDataAuditLetter: CoreInterfaces.TemplateDataAuditLetter = {
    documentType: selectedService.data.code,
    organisationNumber: formatOrganisationNo(
      globalState.currentConfiguration.organisationNumber
    ),
    clientName: globalState.generalVariables.companyName,
    currentYear: new Date().getFullYear(),
    adress: globalState.generalVariables.address,
    postNum: getFormattedPostalNumber(globalState),
    area: globalState.generalVariables.area,
    clientSignee: engagementClientSigneesLeftColumn[0],
    gtSigneeName: gtSigneeName,
    clientSigneesLeft: engagementClientSigneesLeftColumn,
    clientSigneesRight: engagementClientSigneesRightColumn,
    requestDigitalSignature:
      questionDTOMap.Q0017.state.isShown &&
      questionDTOMap.Q0017.data.userValue === Constants.YesNo.Yes,
    isCompany:
      questionDTOMap.QA0009.state.isShown &&
      questionDTOMap.QA0009.data.userValue === Constants.Auditor.AuditCompany,
    accessToFlow:
      questionDTOMap.QA0012.state.isShown &&
      questionDTOMap.QA0012.data.userValue === Constants.YesNo.Yes,
    feeFormat: feeFormatValue,
    showPrice:
      questionDTOMap.QA0014.state.isShown &&
      questionDTOMap.QA0014.data.userValue === Constants.YesNo.Yes,
    priceText: priceText,
    duration: duration,
    endYear: questionDTOMap.QA0017.data.userValue as unknown as number,
    stateFee: new Intl.NumberFormat("sv-SE").format(
      questionDTOMap.QA0018.data.userValue as unknown as number
    ),
    ceoExists:
      questionDTOMap.QA0010.state.isShown &&
      questionDTOMap.QA0010.data.userValue === Constants.YesNo.Yes,
    appliesIFRS:
      questionDTOMap.QA0011.state.isShown &&
      questionDTOMap.QA0011.data.userValue === Constants.YesNo.Yes,
    assigner: assigner,
    assignerValue: t(`Options.${assigner}`, language),
    ruleOption: rule,
    rule: t(`Options.${rule}`, language),
    administrator: t(`Options.${administrator}`, language),
    administratorOption: administrator,
    receiver: t(`Options.${receiver}`, language),
    receiverOption: receiver,
    secondRuleOption: rule2,
    secondRule: t(`Options.${rule2}`, language),
    assigneeDecisionMakingBody: assigneeDecisionMakingBody,
    assigneeDecisionMakingBodyValue: t(
      `Options.${assigneeDecisionMakingBody}`,
      language
    ),
    isLessThan4Years:
      duration === Constants.Years.OneYear ||
      duration === Constants.Years.TwoYear ||
      duration === Constants.Years.ThreeYear,
  };

  return templateDataAuditLetter;
}

function parseContactPersonList(
  contactPersonList: string[] | CoreInterfaces.SigneeDetailsDocxPack[],
  separator: string
) {
  if (contactPersonList.length === 0) return "";

  const getName = (item: string | CoreInterfaces.SigneeDetailsDocxPack) =>
    typeof item === "string" ? item : item.Name;

  const allButLast = contactPersonList.slice(0, -1).map(getName).join(", ");
  const last = getName(contactPersonList[contactPersonList.length - 1]);

  return allButLast + (allButLast ? ` ${separator} ` : "") + last;
}

export function handleTemplateEngagementLetterAppendix(
  globalState: CoreInterfaces.AppState
) {
  const isService7Active = globalState.services.find(
    (service) =>
      service.data.code ===
        Constants.ServiceCode.PayrollAndExpenseAndTravelInvoiceManagement &&
      service.state.isSelected
  );
  const isFirst6ServicesActive = globalState.services.some(
    (service) =>
      [
        Constants.ServiceCode.AccountsPayable,
        Constants.ServiceCode.AccountsReceivable,
        Constants.ServiceCode.CorporateCardManagement,
        Constants.ServiceCode.OtherAccountAndReconciliation,
        Constants.ServiceCode.PeriodReporting,
        Constants.ServiceCode.InterimListedCompanies,
        Constants.ServiceCode.AnnualReporting,
        Constants.ServiceCode.AnnualReportingListedCompanies,
      ].includes(service.data.code) && service.state.isSelected
  );
  const isService10Active = globalState.services.find(
    (service) =>
      Constants.ServiceCode.EngagementCounseling === service.data.code &&
      service.state.isSelected
  );
  const templateDataEngagementLetter = {
    CompanyName: globalState.currentConfiguration.companyName,
    Address: globalState.generalVariables.address,
    PostalNumber: getFormattedPostalNumber(globalState),
    Area: globalState.generalVariables.area,
    OrganisationNo: formatOrganisationNo(
      globalState.currentConfiguration.organisationNumber
    ),
    CurrentYear: new Date().getFullYear(),
    isService7Active: !!isService7Active,
    isFirst6ServicesOr10Active: isFirst6ServicesActive || !!isService10Active,
  };
  return templateDataEngagementLetter;
}

export function handleTemplateTaxCounselingLetter(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO,
  language: Constants.Languages
) {
  const {
    Q0003,
    Q0017,
    Q0020,
    QT097,
    QT099,
    QT101,
    QT102,
    QT104,
    QT105,
    QT107,
    QT109,
    QT110,
    QT112,
    QT116,
    QT117,
    QT118,
    QT119,
    QT123,
    QT124,
    QT127,
    QT128,
    QT129,
    QT130,
    QT131,
    QT136,
    QT139,
    QT140,
    QT144,
    QT144_1,
    QT145,
    QT413,
    QT414,
    QT730,
  } = Constants.GeneralInformationQuestion;
  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    Q0003,
    Q0017,
    Q0020,
    QT097,
    QT099,
    QT101,
    QT102,
    QT104,
    QT105,
    QT107,
    QT109,
    QT110,
    QT112,
    QT116,
    QT117,
    QT118,
    QT119,
    QT123,
    QT124,
    QT127,
    QT128,
    QT129,
    QT130,
    QT131,
    QT136,
    QT139,
    QT140,
    QT144,
    QT144_1,
    QT145,
    QT413,
    QT414,
    QT730,
  ]);
  const separator = t("General.DocumentsSeparator", language);

  const qt104ContactPersonNames = extractGTContactPersonUserDetails(
    globalState,
    questionDTOMap[QT104]
  );
  const assigneePrimary = parseContactPersonList(
    qt104ContactPersonNames.filter((name) => name !== ""),
    separator
  );
  const hasMultipleAssigneesPrimary =
    Array.isArray(questionDTOMap[QT104].data.userValue) &&
    (questionDTOMap[QT104].data.userValue as string[]).length > 1;

  let assigneeSecondary = "";
  if (questionDTOMap.QT105.state.isShown) {
    const qt105ContactPersons = extractGTContactPersonUserDetails(
      globalState,
      questionDTOMap[QT105]
    );
    assigneeSecondary = parseContactPersonList(qt105ContactPersons, separator);
  }

  const companyReportInformationNewList: string[] = [];
  if (questionDTOMap.QT109.state.isShown) {
    (questionDTOMap.QT109.data.userValue as string[]).forEach((value) => {
      companyReportInformationNewList.push(t(`Options.${value}`));
    });
  }
  const clients = questionDTOMap.QT145.data.userValue as ClientDetails[];
  const companyClients = (
    questionDTOMap.QT145.data.userValue as ClientDetails[]
  )
    .filter((client) => client.ClientType === Constants.ClientType.Company)
    .map((client) => ({
      ...client,
      ClientOrgNr: formatOrganisationNo(client.ClientOrgNr),
    }));

  const privateClientsTemp = (
    questionDTOMap.QT145.data.userValue as ClientDetails[]
  )
    .filter(
      (client) => client.ClientType === Constants.ClientType.PrivatePerson
    )
    .map((client) => ({
      ...client,
      ClientOrgNr: formatOrganisationNo(client.ClientOrgNr),
    }));

  const privateClients = privateClientsTemp.map((client) => {
    const hasCompanyClients = companyClients.length > 0;

    const updatedGTSignee = {
      ...client.GTSignee,
      SignerNote: hasCompanyClients
        ? t("General.ClientDetails.SigneeInfo")
        : "",
    };

    return {
      ...client,
      GTSignee: updatedGTSignee,
    };
  });

  let engagementGTSigneesLeftColumn: Array<string> = [];
  let engagementGTSigneesRightColumn: Array<string> = [];

  if (questionDTOMap.QT104.state.isShown) {
    const qT104ContactPersons = mapUserResponseToGTContactPersons(
      globalState,
      questionDTOMap[QT104]
    );
    const qT104ContactPersonNames = qT104ContactPersons.map(
      (contactPersonItem: CoreInterfaces.ContactPersonItem) => ({
        GTSignee: `${contactPersonItem.firstName} ${contactPersonItem.lastName}`,
        GTSigneeRole: !!contactPersonItem.jobName
          ? `${contactPersonItem.jobName}`
          : "",
      })
    );

    [engagementGTSigneesLeftColumn, engagementGTSigneesRightColumn] =
      distributeSigneesAcrossTwoArrays(qT104ContactPersonNames);
  }

  let engagementPrivateClientSigneesLeftColumn: Array<string> = [];
  let engagementPrivateClientSigneesRightColumn: Array<string> = [];

  let engagementCompanyClientSigneesLeftColumn: Array<string> = [];
  let engagementCompanyClientSigneesRightColumn: Array<string> = [];

  if (privateClients.length > 0) {
    const clientsNameWithRole = privateClients.map((value) => {
      const firstName = value.GTSignee.FirstName.trim();
      const lastName = value.GTSignee.LastName.trim();
      const fullName = `${firstName} ${lastName}`;

      if (value.GTSignee.SignerNote) {
        return fullName + "\n" + value.GTSignee.SignerNote;
      }
      return fullName;
    });

    [
      engagementPrivateClientSigneesLeftColumn,
      engagementPrivateClientSigneesRightColumn,
    ] = distributeSigneesAcrossTwoArrays(clientsNameWithRole);
  }

  if (companyClients.length > 0) {
    const signeeMap = new Map();

    companyClients.forEach((client) => {
      client.Signees.forEach((signee) => {
        if (signee.FirstName) {
          const firstName = signee.FirstName.trim();
          const lastName = signee.LastName.trim();
          const signeeFullName = `${firstName} ${lastName}`;

          if (signeeMap.has(signeeFullName)) {
            signeeMap.get(signeeFullName).push(client.ClientName);
          } else {
            signeeMap.set(signeeFullName, [client.ClientName]);
          }
        }
      });
    });

    const mergedSignees = Array.from(signeeMap.entries()).map(
      ([name, clientNames]) => {
        return {
          ClientSignee: name,
          ClientNames: parseContactPersonList(clientNames, separator),
        };
      }
    );

    [
      engagementCompanyClientSigneesLeftColumn,
      engagementCompanyClientSigneesRightColumn,
    ] = distributeSigneesAcrossTwoArrays(mergedSignees);
  }
  let mutualTerminationTime = "";
  if (
    questionDTOMap.QT129.data.userValue ===
      t(`Questions.${questionDTOMap.QT129.data.code}.DefaultValue`, {
        lng: Constants.Languages.SE.toLowerCase(),
      }) ||
    questionDTOMap.QT129.data.userValue ===
      t(`Questions.${questionDTOMap.QT129.data.code}.DefaultValue`, {
        lng: Constants.Languages.EN.toLowerCase(),
      })
  ) {
    mutualTerminationTime = t(
      `Questions.${questionDTOMap.QT129.data.code}.DefaultValue`,
      { lng: language.toLowerCase() }
    );
  } else {
    mutualTerminationTime = questionDTOMap.QT129.data.userValue as string;
  }

  const templateDataEngagementLetter = {
    clients: clients,
    engagementGTSigneesLeftColumn: engagementGTSigneesLeftColumn,
    engagementGTSigneesRightColumn: engagementGTSigneesRightColumn,
    engagementPrivateClientSigneesLeftColumn:
      engagementPrivateClientSigneesLeftColumn,
    engagementPrivateClientSigneesRightColumn:
      engagementPrivateClientSigneesRightColumn,
    engagementCompanyClientSigneesLeftColumn:
      engagementCompanyClientSigneesLeftColumn,
    engagementCompanyClientSigneesRightColumn:
      engagementCompanyClientSigneesRightColumn,
    areSeveralCustomers:
      questionDTOMap.QT097.state.isShown &&
      questionDTOMap.QT097.data.userValue === Constants.YesNo.Yes,
    hasSingleClient: companyClients.length + privateClients.length === 1,
    hasMultipleCompaniesOnly:
      companyClients.length >= 2 && privateClients.length === 0,
    hasMixedClientsWithMultiple:
      companyClients.length >= 1 && privateClients.length >= 1,
    hasMixedClientsAndNoIncomeTaxReturn:
      companyClients.length >= 1 &&
      privateClients.length >= 1 &&
      ((questionDTOMap.QT144.state.isShown &&
        questionDTOMap.QT144.data.userValue === Constants.YesNo.No) ||
        (questionDTOMap.QT144_1.state.isShown &&
          questionDTOMap.QT144_1.data.userValue === Constants.YesNo.No)),
    showSubmissionResponsibilitySection:
      (companyClients.length > 0 &&
        privateClients.length > 0 &&
        !questionDTOMap.QT413.state.isShown) ||
      (questionDTOMap.QT413.state.isShown &&
        questionDTOMap.QT413.data.userValue === Constants.YesNo.No),
    assigneePrimary: assigneePrimary,
    assigneeSecondary: assigneeSecondary,
    hasMultipleAssigneesPrimary: hasMultipleAssigneesPrimary,
    assignmentTitle: t(
      `Options.${questionDTOMap.QT101.data.userValue}`
    ).toLocaleLowerCase(),
    assistantAgentTaxReturn:
      questionDTOMap.QT413.state.isShown &&
      questionDTOMap.QT413.data.userValue === Constants.YesNo.Yes,
    clientResponsibleForSubmission:
      questionDTOMap.QT413.state.isShown &&
      questionDTOMap.QT413.data.userValue === Constants.YesNo.No,
    assistantIncomeTaxReturn:
      (questionDTOMap.QT144.state.isShown &&
        questionDTOMap.QT144.data.userValue === Constants.YesNo.Yes) ||
      (questionDTOMap.QT144_1.state.isShown &&
        questionDTOMap.QT144_1.data.userValue === Constants.YesNo.Yes),
    assistantVatDeclarationRepresentative:
      questionDTOMap.QT730.state.isShown &&
      questionDTOMap.QT730.data.userValue === Constants.YesNo.Yes,
    clientResponsibleForVatSubmission:
      questionDTOMap.QT730.state.isShown &&
      questionDTOMap.QT730.data.userValue === Constants.YesNo.No,
    showTaxReturnSection:
      (companyClients.length > 0 && privateClients.length > 0) ||
      (questionDTOMap.QT144.state.isShown &&
        questionDTOMap.QT144.data.userValue === Constants.YesNo.Yes &&
        !(
          questionDTOMap.QT414.state.isShown &&
          questionDTOMap.QT414.data.userValue ===
            Constants.TaxType.IncomeTaxReturnM2
        )) ||
      (questionDTOMap.QT144_1.state.isShown &&
        questionDTOMap.QT144_1.data.userValue === Constants.YesNo.Yes &&
        !(
          questionDTOMap.QT414.state.isShown &&
          questionDTOMap.QT414.data.userValue ===
            Constants.TaxType.IncomeTaxReturnM2
        )) ||
      (questionDTOMap.QT414.state.isShown &&
        (questionDTOMap.QT414.data.userValue ===
          Constants.TaxType.IncomeTaxReturnM1 ||
          questionDTOMap.QT414.data.userValue ===
            Constants.TaxType.IncomeTaxReturnM3)),
    isVatReturnSelected:
      questionDTOMap.QT414.state.isShown &&
      questionDTOMap.QT414.data.userValue ===
        Constants.TaxType.IncomeTaxReturnM2,
    showVatReturnSection:
      questionDTOMap.QT414.state.isShown &&
      questionDTOMap.QT414.data.userValue ===
        Constants.TaxType.IncomeTaxReturnM2,
    companyReportInformationNew: companyReportInformationNewList,
    currentYear: new Date().getFullYear(),
    companyClients: companyClients,
    consultantFeeLow:
      questionDTOMap.QT139.state.isShown && questionDTOMap.QT139.data.userValue,
    consultantFeeHigh:
      questionDTOMap.QT140.state.isShown && questionDTOMap.QT140.data.userValue,
    privateClients: privateClients,
    date: formatDate(
      Date.now(),
      Constants.HelpfulConstants.DateFormatYearMonthAndDay
    ),
    doesIncludeTaxReturn:
      (questionDTOMap.QT144.state.isShown &&
        questionDTOMap.QT144.data.userValue === Constants.YesNo.Yes) ||
      (questionDTOMap.QT144_1.state.isShown &&
        questionDTOMap.QT144_1.data.userValue === Constants.YesNo.Yes),
    streetAdress: (questionDTOMap.QT145.data.userValue as ClientDetails[])[0]
      .StreetAdress,
    address: formatSwedishPostalCodeInAddressLine(
      (questionDTOMap.QT145.data.userValue as ClientDetails[])[0].Address,
      getCompanyAddressCountry(globalState.remoteData?.companyDetails)
    ),
    firstClientName: (questionDTOMap.QT145.data.userValue as ClientDetails[])[0]
      .ClientName,
    hasPersonalDataAssistant:
      questionDTOMap.QT130.state.isShown &&
      questionDTOMap.QT130.data.userValue === Constants.YesNo.Yes,
    invoicingAlternativeFour:
      questionDTOMap.QT116.state.isShown && questionDTOMap.QT116.data.userValue,
    invoicingEnd: questionDTOMap.QT119.data.userValue,
    invoicingFixed:
      questionDTOMap.QT117.state.isShown && questionDTOMap.QT117.data.userValue,
    invoicingMethodAlternativeOne:
      questionDTOMap.QT112.state.isShown &&
      (questionDTOMap.QT112.data.userValue as string[]).includes(
        Constants.InvoicingMethod.AlternativeOne
      ),
    invoicingMethodAlternativeTwo:
      questionDTOMap.QT112.state.isShown &&
      (questionDTOMap.QT112.data.userValue as string[]).includes(
        Constants.InvoicingMethod.AlternativeTwo
      ),
    invoicingMethodAlternativeFour:
      questionDTOMap.QT112.state.isShown &&
      (questionDTOMap.QT112.data.userValue as string[]).includes(
        Constants.InvoicingMethod.AlternativeFour
      ),
    hasOnlyOngoingBillingMethod:
      questionDTOMap.QT112.state.isShown &&
      (questionDTOMap.QT112.data.userValue as string[]).includes(
        Constants.InvoicingMethod.AlternativeThree
      ) &&
      (questionDTOMap.QT112.data.userValue as string[]).length === 1,
    hasOngoingBillingMethodAndOneMore:
      questionDTOMap.QT112.state.isShown &&
      (questionDTOMap.QT112.data.userValue as string[]).includes(
        Constants.InvoicingMethod.AlternativeThree
      ) &&
      (questionDTOMap.QT112.data.userValue as string[]).length > 1,
    invoicingSeveral:
      questionDTOMap.QT123.state.isShown && questionDTOMap.QT123.data.userValue,
    invoicingStart: questionDTOMap.QT118.data.userValue,
    isActiveOptionForDigitalSigning:
      questionDTOMap.Q0017.state.isShown &&
      questionDTOMap.Q0017.data.userValue === Constants.YesNo.Yes,
    isCustomerCreatedInFlow:
      questionDTOMap.QT110.state.isShown &&
      questionDTOMap.QT110.data.userValue === Constants.YesNo.Yes,
    isAssignmentIncluded:
      questionDTOMap.QT102.state.isShown &&
      questionDTOMap.QT102.data.userValue === Constants.YesNo.Yes,
    isOtherReference:
      questionDTOMap.QT131.state.isShown &&
      questionDTOMap.QT131.data.userValue === Constants.YesNo.Yes,
    isValidityTermination: questionDTOMap.QT124.state.isShown,
    mutualTerminationTime: mutualTerminationTime,
    numberOfSignees:
      engagementCompanyClientSigneesLeftColumn.length +
      engagementCompanyClientSigneesRightColumn.length +
      engagementPrivateClientSigneesLeftColumn.length +
      engagementPrivateClientSigneesRightColumn.length +
      engagementGTSigneesLeftColumn.length +
      engagementGTSigneesRightColumn.length,
    postalCode: formatSwedishPostalCode(
      questionDTOMap.Q0020.data.userValue as string,
      getCompanyAddressCountry(globalState.remoteData?.companyDetails)
    ),
    stipulatedFee:
      questionDTOMap.QT099.state.isShown && questionDTOMap.QT099.data.userValue,
    taxType:
      questionDTOMap.QT414.state.isShown &&
      t(`Options.${questionDTOMap.QT414.data.userValue}`, language),
    timePlanDate:
      questionDTOMap.QT107.state.isShown &&
      formatDate(
        new Date(questionDTOMap.QT107.data.userValue as unknown as string),
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
    timePlanNeeded:
      questionDTOMap.QT136.state.isShown &&
      questionDTOMap.QT136.data.userValue === Constants.YesNo.Yes,
    notTimePlanNeeded:
      questionDTOMap.QT136.state.isShown &&
      questionDTOMap.QT136.data.userValue === Constants.YesNo.No,
    validityTerminationEnd:
      questionDTOMap.QT128.state.isShown &&
      formatDate(
        new Date(questionDTOMap.QT128.data.userValue as unknown as string),
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
    validityTermination:
      questionDTOMap.QT124.state.isShown &&
      questionDTOMap.QT124.data.userValue === Constants.YesNo.Yes,
    validityTerminationStart:
      questionDTOMap.QT127.state.isShown &&
      formatDate(
        new Date(questionDTOMap.QT127.data.userValue as unknown as string),
        Constants.HelpfulConstants.DateFormatYearMonthAndDay
      ),
  };
  return templateDataEngagementLetter;
}

export function handleTemplateTaxCounselingAppendix(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO
) {
  const { QT135, QT135_1, QT138, QT145 } = Constants.GeneralInformationQuestion;
  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    QT135,
    QT135_1,
    QT138,
    QT145,
  ]);

  const companyClientsFiltered = (
    questionDTOMap.QT145.data.userValue as ClientDetails[]
  ).filter((client) => client.ClientType === Constants.ClientType.Company);
  const companyClients = companyClientsFiltered.map((client, index) => ({
    ...client,
    ClientOrgNr: formatOrganisationNo(client.ClientOrgNr),
    IsNotTheLastOne: index != companyClientsFiltered.length - 1,
  }));

  const privateClients = (
    questionDTOMap.QT145.data.userValue as ClientDetails[]
  )
    .filter(
      (client) => client.ClientType === Constants.ClientType.PrivatePerson
    )
    .map((client) => ({
      ...client,
      ClientOrgNr: formatOrganisationNo(client.ClientOrgNr),
    }));

  const appendixDeclarationConsultingList: string[] = [];
  if (questionDTOMap.QT135.state.isShown) {
    (questionDTOMap.QT135.data.userValue as string[]).forEach((value) => {
      appendixDeclarationConsultingList.push(t(`Options.${value}`));
    });
  } else {
    if (questionDTOMap.QT135_1.state.isShown) {
      (questionDTOMap.QT135_1.data.userValue as string[]).forEach((value) => {
        appendixDeclarationConsultingList.push(t(`Options.${value}`));
      });
    }
  }

  const appendixRecipientPersonalDataList: string[] = [];
  if (questionDTOMap.QT138.state.isShown) {
    (questionDTOMap.QT138.data.userValue as string[]).forEach((value) => {
      appendixRecipientPersonalDataList.push(t(`Options.${value}`));
    });
  }
  const templateDataEngagementAppendix = {
    currentYear: new Date().getFullYear(),
    appendixDeclarationConsulting: appendixDeclarationConsultingList,
    appendixRecipientPersonalData: appendixRecipientPersonalDataList,
    companyClients: companyClients,
    privateClients: privateClients,
  };

  return templateDataEngagementAppendix;
}

export function handleTemplateServicesAgreementLetter(
  globalState: CoreInterfaces.AppState,
  genericService: DTOs.ServiceDTO
) {
  const {
    QT701,
    QT702,
    QT703,
    QT704,
    QT705,
    QT706,
    QT707,
    QT708,
    QT709,
    QT710,
    QT713,
    QT714,
    QT715,
    QT716,
    QT717,
    QT718,
    QT720,
    QT721,
    QT722,
    QT723,
    QT724,
    QT725,
  } = Constants.GeneralInformationQuestion;

  const questionDTOMap = mapQuestionCodesToQuestionDTOs(genericService, [
    QT701,
    QT702,
    QT703,
    QT704,
    QT705,
    QT706,
    QT707,
    QT708,
    QT709,
    QT710,
    QT713,
    QT714,
    QT715,
    QT716,
    QT717,
    QT718,
    QT720,
    QT721,
    QT722,
    QT723,
    QT724,
    QT725,
  ]);

  const primaryFirmValue =
    globalState.currentConfiguration?.applicationConfiguration.subsidiaryConfiguration.Subsidiaries.find(
      (subsidiary) => subsidiary.Id === questionDTOMap.QT701.data.userValue
    );
  const secondaryFirmValue =
    globalState.currentConfiguration?.applicationConfiguration.subsidiaryConfiguration.Subsidiaries.find(
      (subsidiary) => subsidiary.Id === questionDTOMap.QT702.data.userValue
    );

  const templateDataEngagementAppendix = {
    currentYear: new Date().getFullYear(),
    date: formatDate(
      new Date(questionDTOMap.QT718.data.userValue as unknown as string),
      Constants.HelpfulConstants.DateFormatYearMonthAndDay
    ),
    deadlineDescription:
      questionDTOMap.QT707.state.isShown && questionDTOMap.QT707.data.userValue,
    primaryFirmValue: primaryFirmValue.CountryEN,
    primaryFirmEmail:
      questionDTOMap.QT704.state.isShown && questionDTOMap.QT704.data.userValue,
    primaryFirmReview:
      questionDTOMap.QT717.state.isShown && questionDTOMap.QT717.data.userValue,
    secondaryFirmValue: secondaryFirmValue.CountryEN,
    secondaryFirmEmail:
      questionDTOMap.QT705.state.isShown && questionDTOMap.QT705.data.userValue,
    serviceApplicableSaleTax:
      questionDTOMap.QT714.state.isShown && questionDTOMap.QT714.data.userValue,
    serviceBillingArrangement:
      questionDTOMap.QT716.state.isShown && questionDTOMap.QT716.data.userValue,
    serviceCurrency:
      questionDTOMap.QT708.state.isShown && questionDTOMap.QT708.data.userValue,
    serviceDisbursement:
      questionDTOMap.QT710.state.isShown && questionDTOMap.QT710.data.userValue,
    serviceFee:
      questionDTOMap.QT709.state.isShown && questionDTOMap.QT709.data.userValue,
    serviceOther:
      questionDTOMap.QT713.state.isShown && questionDTOMap.QT713.data.userValue,
    serviceOtherMatter:
      questionDTOMap.QT706.state.isShown && questionDTOMap.QT706.data.userValue,
    serviceScope:
      questionDTOMap.QT703.state.isShown && questionDTOMap.QT703.data.userValue,
    serviceWithholdingTax:
      questionDTOMap.QT715.state.isShown && questionDTOMap.QT715.data.userValue,
    shouldTheSecondaryFirmFeeIncludeDisbursement:
      questionDTOMap.QT725.state.isShown &&
      questionDTOMap.QT725.data.userValue === Constants.YesNo.Yes,
    shouldTheSecondaryFirmFeeIncludeOtherMatters:
      questionDTOMap.QT723.state.isShown &&
      questionDTOMap.QT723.data.userValue === Constants.YesNo.Yes,
    shouldTheServiceAgreementIncludeDetails:
      questionDTOMap.QT724.state.isShown &&
      questionDTOMap.QT724.data.userValue === Constants.YesNo.Yes,
    shouldTheServiceAgreementIncludeOtherMatters:
      questionDTOMap.QT722.state.isShown &&
      questionDTOMap.QT722.data.userValue === Constants.YesNo.Yes,
    targetclient:
      questionDTOMap.QT720.state.isShown && questionDTOMap.QT720.data.userValue,
  };

  return templateDataEngagementAppendix;
}
